import { useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useAction } from "convex/react";
import { Building2, Loader2 } from "lucide-react";
import { api } from "@constructionautomation/backend";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export function OrganizationOnboardingScreen() {
  const { switchToOrganization } = useAuth();
  const createOrganization = useAction(api.authActions.createOrganization);
  const syncIdentity = useAction(api.authActions.syncIdentityFromWorkos);

  const [orgName, setOrgName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshingAccess, setIsRefreshingAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    const trimmed = orgName.trim();
    if (!trimmed) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const result = await createOrganization({ name: trimmed });
      await switchToOrganization({
        organizationId: result.workosOrganizationId,
      });
      await syncIdentity({});
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshAccess = async () => {
    setIsRefreshingAccess(true);
    setError(null);
    try {
      const result = await syncIdentity({});
      if (!result.requiresOnboarding) {
        window.location.reload();
        return;
      }
      setError("No organization membership was found yet. If you were invited, open the invite email first.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh organization access");
    } finally {
      setIsRefreshingAccess(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md py-4">
        <CardHeader className="pb-2 text-center">
          <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Building2 className="size-6" />
          </div>
          <CardTitle className="text-lg tracking-tight">
            Create or Join an Organization
          </CardTitle>
          <CardDescription>
            Create a new organization, or refresh your access after accepting an invite.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            value={orgName}
            onChange={(event) => setOrgName(event.target.value)}
            placeholder="Organization name"
            disabled={isSubmitting}
            autoFocus
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void handleCreate();
              }
            }}
          />
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <Button
            className="w-full"
            onClick={() => void handleCreate()}
            disabled={isSubmitting || isRefreshingAccess || !orgName.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Organization"
            )}
          </Button>
          <Button
            className="w-full"
            variant="outline"
            onClick={() => void handleRefreshAccess()}
            disabled={isSubmitting || isRefreshingAccess}
          >
            {isRefreshingAccess ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              "Refresh Access"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
